import { css } from '@emotion/react';
import React, { useCallback, useState } from 'react';
import NvDropdown, {
  NvDropdownAlign,
  NvDropdownButtonStyle,
  NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import { NvPopover } from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import {
  black,
  danger,
  gray1,
  gray2,
  gray4,
  gray5,
  highTide,
  primary,
  success,
  warning,
  white,
} from 'styles/global_defaults/colors';
import { boldFontWeight, textSmallFontSize } from 'styles/global_defaults/fonts';
import { halfSpacing, quarterSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';

/** Badge color options for the counter */
export enum BadgeColor {
  WARNING = 'warning',
  DANGER = 'danger',
  SUCCESS = 'success',
  PRIMARY = 'primary',
  GRAY = 'gray',
}

export type NvPillTabConfirmationPopover = {
  show: boolean,
  header: string,
  cancelText: string,
  confirmText: string,
  onCancel: () => void,
  onConfirm: () => void,
};

export type NvPillTabProps = {
  /** Display label for the pill */
  label: string,
  /** Count to show in the badge */
  count: number,
  /** Whether this pill is currently selected/active */
  isSelected: boolean,
  /** Click handler for selecting this pill */
  onClick: () => void,
  /** Unique identifier */
  id: string,
  /** Hover popover content (optional) */
  hoverContent?: React.ReactNode,
  /** 3-dot menu items (optional - if provided, shows the menu) */
  menuItems?: NvDropdownOption[],
  /** data-qa attribute for testing/Pendo */
  dataQA?: string,
  /** Color for the badge counter (default: 'warning' for selected, 'gray' for unselected) */
  badgeColor?: BadgeColor,
  /** Confirmation popover (optional - controlled externally) */
  confirmationPopover?: NvPillTabConfirmationPopover,
  /** Custom popover with arbitrary content (optional - controlled externally) */
  popover?: {
    show: boolean,
    content: React.ReactNode,
  },
};

const PILL_MAX_WIDTH = 200;
const BADGE_SIZE = 22;

// Badge color mapping
const badgeColors: Record<BadgeColor, string> = {
  warning, // #FA6625 - orange
  danger, // #EB0C3B - red
  success, // #00843C - green
  primary, // #0E72ED - blue
  gray: gray1, // #3A424D - dark gray
};


const confirmationPopoverOverlayStyles = css`
  .confirmation .button-container {
    justify-content: center !important;
  }
`;

/**
 * A reusable pill-shaped tab component used for Default Views and Created Views.
 * Supports selected/unselected states, optional 3-dot menu, and hover popovers.
 */
export const NvPillTab = ({
  label,
  count,
  isSelected,
  onClick,
  id,
  hoverContent,
  menuItems,
  dataQA,
  badgeColor,
  confirmationPopover,
  popover,
}: NvPillTabProps) => {
  // Format count for display (handles large numbers)
  const formattedCount = count.toLocaleString();

  // Badge is circular when count < 100, else expands horizontally
  const isLargeNumber = count >= 100;

  // Determine the badge color (gray2 when count is 0)
  const getResolvedBadgeColor = () => {
    if (count === 0) {
      return gray2; // #576373
    }
    if (badgeColor) {
      return badgeColors[badgeColor];
    }
    return isSelected ? warning : gray1;
  };
  const resolvedBadgeColor = getResolvedBadgeColor();

  // Badge hover colors for non-selected state
  const badgeHoverColors: Record<BadgeColor, string> = {
    warning,
    danger,
    success: '#35620D',
    primary: highTide,
    gray: black,
  };
  const getResolvedBadgeHoverColor = () => {
    if (count === 0) return gray2;
    if (badgeColor) return badgeHoverColors[badgeColor];
    return black;
  };
  const resolvedBadgeHoverColor = getResolvedBadgeHoverColor();

  // Determine border and label colors
  // Danger badge in unselected state gets a more "disabled" look
  const getBorderColor = () => {
    if (isSelected) return primary;
    if (badgeColor === 'danger') return gray4;
    return gray5;
  };
  const getLabelColor = () => {
    if (isSelected) return white;
    if (badgeColor === 'danger') return gray2;
    return gray1;
  };
  const borderColor = getBorderColor();
  const labelColor = getLabelColor();

  const handleMenuClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Track if user is hovering over the menu or if dropdown is open to disable popover
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const pillStyles = css`
    display: inline-flex;
    align-items: center;
    gap: ${quarterSpacing}px;
    max-width: ${PILL_MAX_WIDTH}px;
    padding: ${quarterSpacing}px ${quarterSpacing}px ${quarterSpacing}px ${threeQuartersSpacing}px;
    border-radius: 100px;
    border: 1px solid ${borderColor};
    background-color: ${isSelected ? primary : white};
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    font-weight: ${boldFontWeight};
    font-size: ${textSmallFontSize}px;

    &:hover {
      background-color: ${isSelected ? highTide : white};

      .pill-label {
        color: ${isSelected ? white : primary};
      }

      .pill-count-badge {
        ${!isSelected && css`
          background-color: ${resolvedBadgeHoverColor};
          color: ${white};
        `}
      }
    }

    &:focus {
      outline: 2px solid ${warning};
      outline-offset: 2px;
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid ${warning};
      outline-offset: 2px;
    }

    .pill-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: ${labelColor};
      flex-shrink: 1;
      min-width: 0;
    }

    .pill-count-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: ${textSmallFontSize}px;
      font-weight: ${boldFontWeight};
      transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;

      ${isSelected
    ? css`
          background-color: ${white};
          color: ${resolvedBadgeColor};
        `
    : css`
          background-color: ${resolvedBadgeColor};
          color: ${white};
        `}

      ${isLargeNumber
    ? css`
          min-width: ${BADGE_SIZE}px;
          height: ${BADGE_SIZE}px;
          padding: 0 ${halfSpacing}px;
          border-radius: 12px;
        `
    : css`
          width: ${BADGE_SIZE}px;
          height: ${BADGE_SIZE}px;
          border-radius: 50%;
        `}
    }

    .pill-badge-container {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .pill-menu {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${BADGE_SIZE}px;
      height: ${BADGE_SIZE}px;
      border-radius: 50%;
      background-color: ${white};
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;

      .bs4-dropdown {
        .bs4-btn {
          padding: 0;
          min-width: auto;
          color: ${gray1};

          &:hover {
            color: ${black};
          }
        }
        .icon-more {
          transform: rotate(90deg);
          font-size: ${textSmallFontSize}px;
        }
      }
    }

    /* Show menu and hide badge on hover or keyboard focus when menu items exist */
    &:hover, &:focus-within {
      .pill-badge-container.has-menu {
        .pill-count-badge {
          opacity: 0;
          visibility: hidden;
        }
        .pill-menu {
          opacity: 1;
          visibility: visible;
        }
      }
    }

    /* Keep menu visible when dropdown is open */
    .pill-badge-container.has-menu.menu-open {
      .pill-count-badge {
        opacity: 0;
        visibility: hidden;
      }
      .pill-menu {
        opacity: 1;
        visibility: visible;
      }
    }
  `;

  const hasMenu = menuItems && menuItems.length > 0;

  const pillContent = (
    <div
      css={pillStyles}
      className='nv-pill-tab'
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role='tab'
      tabIndex={0}
      aria-selected={isSelected}
      aria-label={`${label}, ${count}`}
      data-qa={dataQA?.toUpperCase()}
      id={id}
    >
      <span className='pill-label' title={label}>
        {label}
      </span>
      <span
        onMouseEnter={() => setIsMenuHovered(true)}
        onMouseLeave={() => setIsMenuHovered(false)}
        className={`pill-badge-container${hasMenu ? ' has-menu' : ''}${isDropdownOpen ? ' menu-open' : ''}`}
      >
        <span className='pill-count-badge'>
          {formattedCount}
        </span>
        {hasMenu && (
          <div
            className='pill-menu'
            onClick={handleMenuClick}
            onKeyDown={(e) => e.stopPropagation()}
            role='presentation'
          >
            <NvDropdown
              items={menuItems}
              buttonStyle={NvDropdownButtonStyle.ICON}
              iconClass='icon-more icon-smallest'
              toggleDataQa={dataQA ? `${dataQA}_MENU` : undefined}
              onToggle={(isOpen) => setIsDropdownOpen(isOpen)}
              container={document.body}
              offset={halfSpacing}
              align={NvDropdownAlign.RIGHT}
            />
          </div>
        )}
      </span>
    </div>
  );

  // Shared popper config to keep popovers within the viewport
  const popoverPopperConfig = {
    modifiers: [
      {
        name: 'preventOverflow',
        enabled: true,
        options: {
          padding: halfSpacing,
        },
      },
    ],
  };

  // Build the final pill, optionally wrapped with hover popover
  let result = pillContent;

  if (hoverContent) {
    result = (
      <NvPopover
        showOnHover
        placement='bottom'
        flip
        popperConfig={popoverPopperConfig}
        content={<>{hoverContent}</>}
        enabled={!isMenuHovered && !isDropdownOpen && !confirmationPopover?.show}
      >
        {result}
      </NvPopover>
    );
  }

  // Wrap with confirmation popover when provided
  if (confirmationPopover) {
    result = (
      <NvPopover
        show={confirmationPopover.show}
        placement='bottom'
        flip
        rootClose
        onHide={confirmationPopover.onCancel}
        popperConfig={popoverPopperConfig}
        overlayStyles={confirmationPopoverOverlayStyles}
        content={(
          <NvConfirmationPopover
            header={confirmationPopover.header}
            message=''
            cancelBtnText={confirmationPopover.cancelText}
            confirmBtnText={confirmationPopover.confirmText}
            onCancel={confirmationPopover.onCancel}
            onConfirm={confirmationPopover.onConfirm}
          />
        )}
      >
        {result}
      </NvPopover>
    );
  }

  // Wrap with custom popover when provided
  if (popover) {
    result = (
      <NvPopover
        show={popover.show}
        placement='bottom'
        flip
        popperConfig={popoverPopperConfig}
        overlayStyles={css`
          width: 350px;
          max-width: 350px;
        `}
        content={<>{popover.content}</>}
      >
        {result}
      </NvPopover>
    );
  }

  return result;
};

export default NvPillTab;
